import { createApi } from '@reduxjs/toolkit/query/react';
import {
  DriverTask,
  CreateDriverTaskBody,
  CreateBulkDriverTasksBody,
  UpdateDriverTaskBody,
  FinishDriverTaskBody,
  CancelDriverTaskBody,
  GetDriverTasksParams,
  GetDriverTasksResponse,
  DriverTaskStats,
  GetDriverTaskStatsResponse,
  BulkCreateResponse
} from '../../resources/types/driverTasksTypes';
import baseQuery from './baseQuery';

export const driverTasksApi = createApi({
  reducerPath: 'driverTasksApi',
  baseQuery,
  tagTypes: ['DriverTask', 'DriverTaskStats'],
  endpoints: builder => ({
    getDriverTasks: builder.query<GetDriverTasksResponse, GetDriverTasksParams>({
      query: params => ({
        url: 'driver_tasks',
        params
      }),
      providesTags: ['DriverTask']
    }),
    getDriverTaskById: builder.query<DriverTask, string>({
      query: id => `driver_tasks/${id}`,
      transformResponse(response: { data: DriverTask }) {
        return response.data;
      },
      providesTags: ['DriverTask']
    }),
    getDriverTaskStats: builder.query<DriverTaskStats, { dateFrom?: string; dateTo?: string } | void>({
      query: (params) => ({
        url: 'driver_tasks/stats',
        params: params || {}
      }),
      transformResponse(response: GetDriverTaskStatsResponse) {
        return response.data;
      },
      providesTags: ['DriverTaskStats']
    }),
    createDriverTask: builder.mutation<DriverTask, CreateDriverTaskBody>({
      query: body => ({
        url: 'driver_tasks',
        method: 'POST',
        body
      }),
      transformResponse(response: { data: DriverTask }) {
        return response.data;
      },
      invalidatesTags: ['DriverTask', 'DriverTaskStats']
    }),
    createBulkDriverTasks: builder.mutation<BulkCreateResponse, CreateBulkDriverTasksBody>({
      query: body => ({
        url: 'driver_tasks/bulk',
        method: 'POST',
        body
      }),
      transformResponse(response: { data: BulkCreateResponse }) {
        return response.data;
      },
      invalidatesTags: ['DriverTask', 'DriverTaskStats']
    }),
    updateDriverTask: builder.mutation<DriverTask, { id: string; body: UpdateDriverTaskBody }>({
      query: ({ id, body }) => ({
        url: `driver_tasks/${id}`,
        method: 'PATCH',
        body
      }),
      transformResponse(response: { data: DriverTask }) {
        return response.data;
      },
      invalidatesTags: ['DriverTask', 'DriverTaskStats']
    }),
    startDriverTask: builder.mutation<DriverTask, string>({
      query: id => ({
        url: `driver_tasks/${id}/start`,
        method: 'PATCH',
        body: {}
      }),
      transformResponse(response: { data: DriverTask }) {
        return response.data;
      },
      invalidatesTags: ['DriverTask', 'DriverTaskStats']
    }),
    finishDriverTask: builder.mutation<DriverTask, { id: string; body: FinishDriverTaskBody }>({
      query: ({ id, body }) => ({
        url: `driver_tasks/${id}/finish`,
        method: 'PATCH',
        body
      }),
      transformResponse(response: { data: DriverTask }) {
        return response.data;
      },
      invalidatesTags: ['DriverTask', 'DriverTaskStats']
    }),
    cancelDriverTask: builder.mutation<DriverTask, { id: string; body?: CancelDriverTaskBody }>({
      query: ({ id, body }) => ({
        url: `driver_tasks/${id}/cancel`,
        method: 'PATCH',
        body: body || {}
      }),
      transformResponse(response: { data: DriverTask }) {
        return response.data;
      },
      invalidatesTags: ['DriverTask', 'DriverTaskStats']
    }),
    deleteDriverTask: builder.mutation<void, string>({
      query: id => ({
        url: `driver_tasks/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['DriverTask', 'DriverTaskStats']
    })
  })
});

export const {
  useGetDriverTasksQuery,
  useLazyGetDriverTasksQuery,
  useGetDriverTaskByIdQuery,
  useGetDriverTaskStatsQuery,
  useCreateDriverTaskMutation,
  useCreateBulkDriverTasksMutation,
  useUpdateDriverTaskMutation,
  useStartDriverTaskMutation,
  useFinishDriverTaskMutation,
  useCancelDriverTaskMutation,
  useDeleteDriverTaskMutation
} = driverTasksApi;
